import * as React from "react";
import * as queryString from "query-string";

interface UrlStateConsumerInterface {
  hasInitializedQueryState: boolean;
  read: () => void;
  update: (partial_state_update: Record<string, any>) => void;
  updateOnlyPartially: (partial_state_update: Record<string, any>) => void;
}

const defaultValue = {} as unknown as UrlStateConsumerInterface;

const UrlStateContext = React.createContext(defaultValue);

export interface UrlStateProviderProps {
  query_read_parsing_stringify_arguments: queryString.ParseOptions;
  sort_delegate: (a: string, b: string) => number;
  readers: ((key: string, value: string | string[] | null) => void)[];
  query_update_parsing_stringify_arguments: queryString.ParseOptions;
  query_update_stringify_arguments: queryString.StringifyOptions;
  nextState: (
    currentUrlDataQuery: queryString.ParsedQuery<string>,
    partial_state_update: Record<string, any>
  ) => Record<string, any>;
  nextState_fromAPartialUpdate: (
    currentUrlDataQuery: queryString.ParsedQuery<string>,
    partial_state_update: Record<string, any>
  ) => Record<string, any>;
}

export const UrlStateProvider: React.FunctionComponent<
  UrlStateProviderProps
> = (props) => {
  const [hasInitializedQueryState, setHasInitializedQueryState] =
    React.useState(false);

  const read = React.useCallback(() => {
    const currentLocationInFull = "" + window.location;

    const currentUrlData = queryString.parseUrl(currentLocationInFull, {
      ...props.query_read_parsing_stringify_arguments,
    });

    const queryEntries = Object.entries(currentUrlData.query).sort(
      (
        a,
        b
      ) => {
        const [keyA] = a;
        const [keyB] = b;
        return props.sort_delegate(keyA, keyB);
      }
    ) as unknown as [string, string | string[] | null][];

    for (const [key, value] of queryEntries) {
      props.readers.forEach((reader) => reader(key, value));
    }

    setHasInitializedQueryState(true);
  }, [props]);

  const update = React.useCallback(
    (partial_state_update: Record<string, any>) => {
      const currentLocationInFull = "" + window.location;

      const currentUrlData = queryString.parseUrl(currentLocationInFull, {
        ...props.query_update_parsing_stringify_arguments,
      });

      const currentUrlDataQuery = currentUrlData?.query;

      const nextUrlData = props.nextState(
        currentUrlDataQuery,
        partial_state_update
      );

      const newQueryValue = queryString.stringify(
        { ...nextUrlData },
        {
          ...props.query_update_stringify_arguments,
          ...{
            sort: props.sort_delegate,
          },
        }
      );

      const newQuery = newQueryValue ? `?${newQueryValue}` : "";

      const newUrl = `${window.location.protocol}//${window.location.host}${window.location.pathname}${newQuery}`;

      window.history.replaceState({ path: newUrl }, "", newUrl);
    },
    [
      props,
      props.nextState,
      props.query_update_parsing_stringify_arguments,
      props.query_update_stringify_arguments,
      props.sort_delegate,
    ]
  );

  const updateOnlyPartially = React.useCallback(
    (partial_state_update: Record<string, any>) => {
      const currentLocationInFull = "" + window.location;

      const currentUrlData = queryString.parseUrl(currentLocationInFull, {
        ...props.query_update_parsing_stringify_arguments,
      });

      const currentUrlDataQuery = currentUrlData?.query;

      const nextUrlData = props.nextState_fromAPartialUpdate(
        currentUrlDataQuery,
        partial_state_update
      );

      const newQueryValue = queryString.stringify(
        { ...nextUrlData },
        {
          ...props.query_update_stringify_arguments,
          ...{
            sort: props.sort_delegate,
          },
        }
      );

      const newQuery = newQueryValue ? `?${newQueryValue}` : "";

      const newUrl = `${window.location.protocol}//${window.location.host}${window.location.pathname}${newQuery}`;

      window.history.replaceState({ path: newUrl }, "", newUrl);
    },
    [
      props,
      props.nextState_fromAPartialUpdate,
      props.query_update_parsing_stringify_arguments,
      props.query_update_stringify_arguments,
      props.sort_delegate,
    ]
  );

  const value: UrlStateConsumerInterface = React.useMemo(
    () => ({
      hasInitializedQueryState,
      read,
      update,
      updateOnlyPartially,
    }),
    [hasInitializedQueryState, read, update, updateOnlyPartially]
  );

  return (
    <UrlStateContext.Provider value={value}>
      {props?.children || []}
    </UrlStateContext.Provider>
  );
};

export const useUrlState = () => {
  const context = React.useContext(UrlStateContext);
  if (context === defaultValue) {
    throw new Error(
      "useUrlStateProvider must be used within a UrlStateProvider"
    );
  }
  return context;
};
function nextState() {
  throw new Error("Function not implemented.");
}
